import type { ChecklistParallelDefinition } from './checklist-parallel';
import type { ChecklistSubsetDefinition } from './checklist-source';
import type { CardIdentity } from './types';

/** Bound by the caller to one exact source row; never an aggregate of candidates. */
export interface ChecklistCardApplicabilityContext {
  sourceSystem: string;
  sourceTargetId: string;
  subset: string;
  number: string;
  familyId?: string;
  rosterPresent: boolean;
}

export type ChecklistApplicabilityReason =
  | 'SOURCE_SCOPE_MISSING'
  | 'SOURCE_SCOPE_CONFLICT'
  | 'SUBSET_MISSING'
  | 'SUBSET_CONFLICT'
  | 'FAMILY_MISSING'
  | 'FAMILY_CONFLICT'
  | 'NUMBER_REQUIRED'
  | 'NUMBER_EXCLUDED'
  | 'APPLICABILITY_UNKNOWN'
  | 'ROSTER_ABSENT';

export interface ChecklistApplicabilityResult {
  decision: 'allowed' | 'unknown' | 'conflict';
  reason?: ChecklistApplicabilityReason;
}

const exact = (value: string | undefined): string => (value ?? '').trim();

/** Restrictions run before uncertainty: unknown confidence never overrides a conflict. */
export function decideChecklistParallelApplicability(
  context: ChecklistCardApplicabilityContext,
  definition: ChecklistParallelDefinition,
): ChecklistApplicabilityResult {
  if (!exact(context.sourceSystem) || !exact(context.sourceTargetId)) {
    return { decision: 'unknown', reason: 'SOURCE_SCOPE_MISSING' };
  }
  if (
    exact(context.sourceSystem) !== exact(definition.source.system) ||
    exact(context.sourceTargetId) !== exact(definition.source.targetId)
  ) {
    return { decision: 'conflict', reason: 'SOURCE_SCOPE_CONFLICT' };
  }
  const subset = exact(context.subset);
  const applicableSubset = exact(definition.applicability.subset);
  if (!subset) return { decision: 'unknown', reason: 'SUBSET_MISSING' };
  if (applicableSubset && subset !== applicableSubset) {
    return { decision: 'conflict', reason: 'SUBSET_CONFLICT' };
  }
  const family = exact(context.familyId);
  const definitionFamily = exact(definition.source.familyId);
  if (family && definitionFamily && family !== definitionFamily) {
    return { decision: 'conflict', reason: 'FAMILY_CONFLICT' };
  }
  const rule = definition.applicability;
  if (rule.kind === 'explicit-cards') {
    if (!exact(context.number)) return { decision: 'conflict', reason: 'NUMBER_REQUIRED' };
    if (!rule.numbers.some((number) => exact(number) === exact(context.number))) {
      return { decision: 'conflict', reason: 'NUMBER_EXCLUDED' };
    }
  }
  if (!applicableSubset || rule.kind === 'unknown') {
    return { decision: 'unknown', reason: 'APPLICABILITY_UNKNOWN' };
  }
  if (context.rosterPresent && (!family || !definitionFamily)) {
    return { decision: 'unknown', reason: 'FAMILY_MISSING' };
  }
  if (definition.applicabilityConfidence === 'unknown') {
    return { decision: 'unknown', reason: 'APPLICABILITY_UNKNOWN' };
  }
  return context.rosterPresent
    ? { decision: 'allowed' }
    : { decision: 'unknown', reason: 'ROSTER_ABSENT' };
}

/** Explicit resolved metadata only; a variation string is not intrinsic-treatment proof. */
export interface ChecklistIntrinsicTreatment {
  definitionId?: string;
  name: string;
}

export function decideChecklistPlainVersion(input: {
  subsetDefinition?: ChecklistSubsetDefinition;
  policyComplete: boolean;
  intrinsicTreatment?: ChecklistIntrinsicTreatment;
}): {
  decision: 'allowed' | 'requires-parallel' | 'unknown';
  reason?: 'SUBSET_POLICY_UNAVAILABLE' | 'PARALLEL_REQUIRED' | 'INTRINSIC_TREATMENT';
} {
  if (
    !input.policyComplete ||
    !input.subsetDefinition ||
    (input.subsetDefinition.noPlainVersion !== undefined &&
      typeof input.subsetDefinition.noPlainVersion !== 'boolean')
  ) {
    return { decision: 'unknown', reason: 'SUBSET_POLICY_UNAVAILABLE' };
  }
  if (input.intrinsicTreatment && exact(input.intrinsicTreatment.name)) {
    return { decision: 'allowed', reason: 'INTRINSIC_TREATMENT' };
  }
  return input.subsetDefinition.noPlainVersion === true
    ? { decision: 'requires-parallel', reason: 'PARALLEL_REQUIRED' }
    : { decision: 'allowed' };
}

export interface ComposeChecklistSelectionInput {
  /** Effective source facts, after durable corrections have been applied. */
  rowIdentity: CardIdentity;
  rowTreatment?: ChecklistIntrinsicTreatment;
  /** Resolved source definition kind, never inferred from variation text. */
  rowTreatmentStatus?: 'PLAIN' | 'INTRINSIC' | 'UNKNOWN';
  correctedFields: readonly string[];
  parallel?: ChecklistParallelDefinition;
  observed?: { identity: CardIdentity; sameQualifiedRowAndTreatment: boolean };
}

export interface ChecklistSelectionIdentityChange {
  field: keyof CardIdentity;
  before: CardIdentity[keyof CardIdentity] | undefined;
  after: CardIdentity[keyof CardIdentity] | undefined;
}

export type ComposeChecklistSelectionResult =
  | { ok: true; identity: CardIdentity; changes: ChecklistSelectionIdentityChange[] }
  | {
      ok: false;
      reason:
        | 'SECOND_TREATMENT'
        | 'ROW_TREATMENT_UNRESOLVED'
        | 'ROW_VARIATION_CONFLICT'
        | 'SERIAL_TO_CONFLICT'
        | 'INVALID_SERIAL_TO';
      fields: (keyof CardIdentity)[];
    };

const validDenominator = (value: number): boolean =>
  Number.isInteger(value) && value >= 1 && value <= 1_000_000;

/** Compose one row plus at most one treatment. Does not establish applicability/lineage. */
export function composeChecklistSelectionIdentity(
  input: ComposeChecklistSelectionInput,
): ComposeChecklistSelectionResult {
  const { rowIdentity: row, parallel, rowTreatment } = input;
  const corrected = new Set(input.correctedFields);
  const status = input.rowTreatmentStatus ?? (rowTreatment ? 'INTRINSIC' : 'UNKNOWN');
  const intrinsic =
    status === 'INTRINSIC' && rowTreatment !== undefined && exact(rowTreatment.name) !== '';
  if ((status === 'INTRINSIC' && !intrinsic) || (rowTreatment && status !== 'INTRINSIC')) {
    return { ok: false, reason: 'ROW_TREATMENT_UNRESOLVED', fields: ['variation'] };
  }
  const sameTreatment =
    intrinsic &&
    parallel !== undefined &&
    exact(rowTreatment.name) === exact(parallel.name) &&
    (rowTreatment.definitionId === undefined ||
      exact(rowTreatment.definitionId) === exact(parallel.definitionId));
  if (parallel && exact(row.variation) && !intrinsic && !corrected.has('variation')) {
    if (status === 'UNKNOWN') {
      return { ok: false, reason: 'ROW_TREATMENT_UNRESOLVED', fields: ['variation'] };
    }
    if (exact(row.variation) !== exact(parallel.name)) {
      return { ok: false, reason: 'ROW_VARIATION_CONFLICT', fields: ['variation'] };
    }
  }
  if (intrinsic && parallel && !sameTreatment) {
    return { ok: false, reason: 'SECOND_TREATMENT', fields: ['variation'] };
  }
  if (
    (row.serialTo !== undefined && !validDenominator(row.serialTo)) ||
    (parallel?.serialTo !== undefined && !validDenominator(parallel.serialTo))
  ) {
    return { ok: false, reason: 'INVALID_SERIAL_TO', fields: ['serialTo'] };
  }
  if (
    !corrected.has('serialTo') &&
    !intrinsic &&
    row.serialTo !== undefined &&
    parallel?.serialTo !== undefined &&
    row.serialTo !== parallel.serialTo
  ) {
    return { ok: false, reason: 'SERIAL_TO_CONFLICT', fields: ['serialTo'] };
  }
  const identity: CardIdentity = { ...row };
  delete identity.serialNumber;
  if (!corrected.has('variation')) {
    if (intrinsic) identity.variation = rowTreatment.name;
    else if (parallel) identity.variation = parallel.name;
  }
  if (
    !corrected.has('serialTo') &&
    row.serialTo === undefined &&
    parallel?.serialTo !== undefined
  ) {
    identity.serialTo = parallel.serialTo;
  }
  if (!corrected.has('serialNumbered')) {
    if (identity.serialTo !== undefined) identity.serialNumbered = true;
    else if (corrected.has('serialTo')) delete identity.serialNumbered;
  }
  for (const field of ['autograph', 'memorabilia'] as const) {
    if (!corrected.has(field) && parallel?.[field] === true) identity[field] = true;
  }
  const observed = input.observed;
  if (
    observed?.sameQualifiedRowAndTreatment &&
    observed.identity.serialTo === identity.serialTo &&
    identity.serialTo !== undefined &&
    Number.isInteger(observed.identity.serialNumber) &&
    observed.identity.serialNumber! >= 1 &&
    observed.identity.serialNumber! <= identity.serialTo
  ) {
    identity.serialNumber = observed.identity.serialNumber;
  }
  const keys = new Set([...Object.keys(row), ...Object.keys(identity)] as (keyof CardIdentity)[]);
  const changes = [...keys]
    .sort()
    .filter((field) => row[field] !== identity[field])
    .map((field) => ({ field, before: row[field], after: identity[field] }));
  return { ok: true, identity, changes };
}
